import axios, { type InternalAxiosRequestConfig } from "axios";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MAIN_BASE_URL = (import.meta as any).env.VITE_API_URL;

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;
let refreshPromise: Promise<unknown> | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  onUnauthorized = handler;
}

const isRefreshRequest = (url?: string) => url?.includes("/auth/refresh");
const shouldAttemptRefresh = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;

  const data = error.response?.data;
  return (
    error.response?.status === 401 &&
    (data?.AUTH_FAIL_CODE === "TOKEN_EXPIRED" ||
      data?.AUTH_FAIL_CODE === "TOKEN_INVALID")
  );
};

// 1. The base instance
const main = axios.create({
  baseURL: MAIN_BASE_URL,
  withCredentials: true,
  timeout: 8000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 2. Add Response Interceptor (Silent Refresh Logic)
main.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (
      shouldAttemptRefresh(error) &&
      !originalRequest._retry &&
      !isRefreshRequest(originalRequest.url)
    ) {
      originalRequest._retry = true;
      try {
        refreshPromise ??= main
          .post(`/auth/refresh`)
          .finally(() => {
            refreshPromise = null;
          });

        await refreshPromise;

        return main(originalRequest);
      } catch (refreshError) {
        if (axios.isAxiosError(refreshError)) {
          const status = refreshError.response?.status;

          if (status === 401 || status === 403) {
            onUnauthorized?.();
          }
        }

        return Promise.reject(refreshError);
      }
    }

    if (
      axios.isAxiosError(error) &&
      (error.response?.status === 401 || error.response?.status === 403) &&
      !isRefreshRequest(originalRequest.url)
    ) {
      onUnauthorized?.();
    }

    return Promise.reject(error);
  },
);

const API = { main };
export default API;
